import React, {forwardRef, ReactElement, ReactNode, useCallback, useMemo, useState} from "react"
import {DropdownList} from "../DropdownList/DropdownList"
import {DropdownBase, DropdownBaseProps, Position} from "../../DropdownBase/DropdownBase"
import {Button, ButtonProps} from "../../Button/Button"
import styles from "./DropdownMenu.module.scss"
import classNames from "classnames"
import CSS from "csstype"
import {Dropdown} from "../../Dropdown/Dropdown"

export interface DropdownMenuItemProps {
    label: string | ReactNode
    description?: string | ReactNode

    isTitle?: boolean

    onClick?: (item?: any) => void
    element?: ReactElement
    access?: string
}

export interface DropdownMenuProps {
    label: string | ReactNode
    position?: Position | Array<Position>
    triggerProps?: Omit<ButtonProps, "active">
    items?: Array<DropdownMenuItemProps>
    open?: boolean
    setOpen?: (param: boolean) => void
    buttonStyle?: CSS.Properties
    whiteStyles?: boolean
    trigger?: ReactElement
    baseDropdownProps?: Partial<DropdownBaseProps>
    widthList?: number
    maxWidthList?: number
    descriptionMedium?: boolean
    fullWidthBlueTrigger?: boolean
    fullWidthGreyTrigger?: boolean
    importantRedDropdown?: boolean
    resetOptionStyles?: boolean
    className?: string
    itemInnerWrapperClassName?: string
    basedOnComponent?: "dropdown" | "dropdown_base"
    blackTextColor?: boolean
    withScrollbars?: boolean
    maxHeight?: string
    dataTest?: string
}

export const DropdownMenu: React.FC<{children?: ReactNode} & DropdownMenuProps> = (props) => {
    const {
        label,
        position,
        items,
        open,
        setOpen,
        buttonStyle,
        trigger,
        baseDropdownProps,
        widthList,
        maxWidthList,
        whiteStyles,
        descriptionMedium,
        children,
        fullWidthBlueTrigger,
        fullWidthGreyTrigger,
        importantRedDropdown,
        resetOptionStyles,
        className,
        itemInnerWrapperClassName,
        basedOnComponent = "dropdown_base",
        blackTextColor,
        withScrollbars,
        maxHeight,
        dataTest,
    } = props
    const [localOpen, localSetOpen] = useState(false)

    const DropdownComponent = basedOnComponent === "dropdown_base" ? DropdownBase : Dropdown

    const TriggerButton = forwardRef<HTMLButtonElement, {}>((triggerProps, ref) => {
        return (
            <>
                {fullWidthBlueTrigger || fullWidthGreyTrigger ? (
                    <button
                        ref={ref}
                        className={classNames(styles.fullWidthTrigger, className, {
                            [styles.fullWidthTrigger_active]: open || localOpen,
                            [styles.redDropdown]: importantRedDropdown,
                            [styles.greyTrigger]: fullWidthGreyTrigger,
                            [styles.blackTextColor]: blackTextColor,
                            [styles.disabled]: baseDropdownProps?.disabled,
                        })}
                        data-cy={"dropdown-trigger"}
                        data-test={dataTest}
                        type={"button"}
                        {...triggerProps}
                    >
                        <div data-test={"dropdown-trigger-label"}>{label}</div>
                    </button>
                ) : (
                    <Button
                        buttonRef={ref}
                        active={open || localOpen}
                        dropdown
                        primary={!whiteStyles}
                        whiteStyles={whiteStyles}
                        style={buttonStyle}
                        data-cy={"dropdown-trigger"}
                        data-test={dataTest}
                        {...triggerProps}
                    >
                        <div data-test={"dropdown-trigger-label"}>{label}</div>
                    </Button>
                )}
            </>
        )
    })

    const close = useCallback(() => (setOpen ? setOpen(false) : localSetOpen(false)), [setOpen])

    const handleClick = useCallback(
        (item: DropdownMenuItemProps) => {
            if (item.onClick) {
                item.onClick(item)
            }

            close()
        },
        [close]
    )

    const itemContentOffsetCN = resetOptionStyles ? undefined : styles.itemContentOffset

    const getItem = useCallback(
        (item: DropdownMenuItemProps, index: number | string) => {
            return item.isTitle ? (
                <div
                    className={classNames(styles["item-title"], styles.itemContent, {
                        [styles["first"]]: !index,
                    })}
                    key={index}
                >
                    <div className={itemContentOffsetCN}>{item.label}</div>
                </div>
            ) : item.element ? (
                <div onClick={close} key={index} className={styles.pointer}>
                    <div className={classNames(itemContentOffsetCN, itemInnerWrapperClassName)}>
                        {item.element}
                    </div>
                </div>
            ) : (
                <div className={styles.itemContent} key={index} onClick={() => handleClick(item)}>
                    <div
                        className={classNames(itemContentOffsetCN, {
                            [styles.contentWithDescr]: item.description,
                        })}
                    >
                        {<span className={styles.label}>{item.label}</span>}
                        {item.description ? (
                            typeof item.description === "string" ? (
                                <span
                                    className={classNames(styles.description, {
                                        [styles.medium]: descriptionMedium,
                                    })}
                                >
                                    {item.description}
                                </span>
                            ) : (
                                item.description
                            )
                        ) : null}
                    </div>
                </div>
            )
        },
        [close, descriptionMedium, handleClick, itemContentOffsetCN]
    )

    const dropdownItems = useMemo(
        () => (items ? items.map((item, index) => getItem(item, index)) : []),
        [items, getItem]
    )

    return (
        //@ts-ignore
        <DropdownComponent
            open={open || localOpen}
            setOpen={setOpen || localSetOpen}
            position={(position || "bottom left") as any}
            marginTop={"0.5rem"}
            trigger={trigger || <TriggerButton {...props.triggerProps} />}
            dropdownClass={
                resetOptionStyles && basedOnComponent === "dropdown" ? styles.noPadding : undefined
            }
            maxHeight={maxHeight}
            {...baseDropdownProps}
        >
            {children ? (
                <>{children}</>
            ) : (
                <DropdownList
                    width={widthList}
                    maxWidth={maxWidthList}
                    items={dropdownItems}
                    withScrollbars={withScrollbars}
                />
            )}
        </DropdownComponent>
    )
}
